import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Grid,
} from '@mui/material';
import { ArrowBack, Business } from '@mui/icons-material';

const SPECIALTIES = [
  'Civil Engineering',
  'Electrical Works',
  'Plumbing & Water Supply',
  'Irrigation Systems',
  'Land Clearing & Preparation',
  'Construction & Building',
  'Pest Control',
  'Agronomy Consulting',
  'Veterinary Services',
  'Transportation & Logistics',
  'Equipment Maintenance',
  'IT & Technology',
  'Security Services',
  'Catering & Food Services',
  'Other',
];
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorService } from '@smartgrow/shared';
import type { CreateContractorRequest, LaborType } from '@smartgrow/shared/types';

const initial: CreateContractorRequest = {
  contractorCode: undefined,
  companyName: '',
  contactPerson: '',
  email: '',
  phone: '',
  address: '',
  specialty: '',
  laborType: 'FORMAL',
  notes: '',
};

export const NewContractorPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateContractorRequest>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateContractorRequest, string>>>({});

  const mutation = useMutation({
    mutationFn: (data: CreateContractorRequest) =>
      contractorService.createContractor({
        ...data,
        contractorCode: data.contractorCode?.trim() || undefined,
        contactPerson: data.contactPerson || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
        specialty: data.specialty || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contractors'] });
      navigate('/dashboard/contractors');
    },
  });

  const validate = () => {
    const e: typeof errors = {};
    if (form.contractorCode && form.contractorCode.length > 20) e.contractorCode = 'Contractor code must be 20 characters or less';
    if (!form.companyName.trim()) e.companyName = 'Company name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: keyof CreateContractorRequest, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="large">
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight="bold">Add Contractor</Typography>
          <Typography variant="body2" color="text.secondary">Register a new contractor or service provider</Typography>
        </Box>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to create contractor. The contractor code or email may already exist.
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Business color="primary" />
          <Typography variant="h6" fontWeight="bold">Contractor Details</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contractor Code (auto-generated if blank)"
              value={form.contractorCode ?? ''}
              onChange={(e) => set('contractorCode', e.target.value.toUpperCase())}
              fullWidth
              error={!!errors.contractorCode}
              helperText={errors.contractorCode || 'Leave blank to auto-generate (e.g. CONT-0001)'}
              inputProps={{ maxLength: 20 }}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name *"
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
              fullWidth
              error={!!errors.companyName}
              helperText={errors.companyName}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Specialty"
              value={form.specialty ?? ''}
              onChange={(e) => set('specialty', e.target.value)}
              fullWidth
              size="medium"
            >
              <MenuItem value="">— Select specialty —</MenuItem>
              {SPECIALTIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Person"
              value={form.contactPerson ?? ''}
              onChange={(e) => set('contactPerson', e.target.value)}
              fullWidth
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              value={form.phone ?? ''}
              onChange={(e) => set('phone', e.target.value)}
              fullWidth
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Labor Type"
              value={form.laborType ?? 'FORMAL'}
              onChange={(e) => set('laborType', e.target.value as LaborType)}
              fullWidth
              size="medium"
              helperText="Casual/day laborers are tracked separately in labor-cost reports"
            >
              <MenuItem value="FORMAL">Formal Contractor</MenuItem>
              <MenuItem value="CASUAL_DAY_LABOR">Casual / Day Labor</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              type="email"
              value={form.email ?? ''}
              onChange={(e) => set('email', e.target.value)}
              fullWidth
              size="medium"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              value={form.address ?? ''}
              onChange={(e) => set('address', e.target.value)}
              fullWidth
              size="medium"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes (optional)"
              multiline
              rows={3}
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth
              inputProps={{ maxLength: 500 }}
              size="medium"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 4 }}>
        <Button
          size="large"
          onClick={() => navigate('/dashboard/contractors')}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={() => validate() && mutation.mutate(form)}
          disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 180 }}
        >
          {mutation.isPending ? 'Creating...' : 'Add Contractor'}
        </Button>
      </Box>
    </Box>
  );
};
